////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Scroll-triggered entrance wrappers — the staggerGroup/fadeRise  //
//           dialect (1.0.3.md §2/§6), applied consistently across every     //
//           public content page.                                           //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Every non-hero content section on About/Contact/Careers/Legal/News/Projects/
//  NotFound goes through one of these rather than each page re-deriving its own
//  reduceVariants()/whileInView/viewport wiring by hand — that hand-rolling is
//  exactly the drift 1.0.3.md §2/§7 rules out ("no component defines its own
//  curve"). This file adds NO new easing/duration/stagger constant of its own —
//  every variant used below comes straight from lib/motion.ts.
//
//  <Reveal>      — a single fadeRise (or `variants` override, e.g. `clipWipe`
//                   for imagery) entrance, `whileInView` once, for one
//                   section/tile that should animate on its own beat.
//  <RevealGroup>  — a staggerGroup container. Wrap a run of sibling
//                   <RevealItem>s (a card grid, a stack of tiles) so they read
//                   as one entrance rather than N independent ones.
//  <RevealItem>   — a child of <RevealGroup> — deliberately carries NO
//                   `initial`/`whileInView` of its own; it inherits the
//                   group's animation state via framer-motion's variant
//                   propagation (a motion component with `variants` but no
//                   explicit animate control message picks up the nearest
//                   ancestor's), which is what makes the group's
//                   staggerChildren apply to it. Default variant is
//                   `fadeRise`; pass `variants={clipWipe}` for card imagery.
//
////////////////////////////////////////////////////////////////////////////////

import type { ReactNode } from 'react';
import { motion, type Variants } from 'framer-motion';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { fadeRise, reduceVariants, staggerGroup, VIEWPORT_ONCE } from '@/lib/motion';

// A fixed, typed set of tags — deliberately not a generic `as={Component}` +
// cast (that would need an unchecked cast to satisfy framer-motion's
// `motion.create`/`motion[tag]` typings, which the house rules rule out). Every
// call site in this pass only ever needs one of these six.
const MOTION_TAGS = {
  div: motion.div,
  section: motion.section,
  article: motion.article,
  nav: motion.nav,
  ul: motion.ul,
  li: motion.li,
} as const;

export type RevealTag = keyof typeof MOTION_TAGS;

export interface RevealProps {
  children: ReactNode;
  /** Defaults to `fadeRise` — pass a different lib/motion.ts variant (e.g.
   * `clipWipe` for a hero image, or `crossfadeContent`) when the section's
   * content calls for it. */
  variants?: Variants;
  as?: RevealTag;
  className?: string;
  id?: string;
}

export function Reveal ({ children, variants = fadeRise, as = 'div', className, id }: RevealProps) {
  const reducedMotion = useReducedMotion();
  const MotionTag = MOTION_TAGS[as];
  return (
    <MotionTag
      id={id}
      className={className}
      variants={reduceVariants(variants, reducedMotion)}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE}
    >
      {children}
    </MotionTag>
  );
}

export interface RevealGroupProps {
  children: ReactNode;
  as?: RevealTag;
  className?: string;
}

export function RevealGroup ({ children, as = 'div', className }: RevealGroupProps) {
  const reducedMotion = useReducedMotion();
  const MotionTag = MOTION_TAGS[as];
  return (
    <MotionTag
      className={className}
      variants={reduceVariants(staggerGroup, reducedMotion)}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE}
    >
      {children}
    </MotionTag>
  );
}

export interface RevealItemProps {
  children: ReactNode;
  variants?: Variants;
  as?: RevealTag;
  className?: string;
}

export function RevealItem ({ children, variants = fadeRise, as = 'div', className }: RevealItemProps) {
  const reducedMotion = useReducedMotion();
  const MotionTag = MOTION_TAGS[as];
  return (
    <MotionTag className={className} variants={reduceVariants(variants, reducedMotion)}>
      {children}
    </MotionTag>
  );
}
